/**
 * Progresso composto do brinde (mesma regra de gift-system.js).
 * Valor preenche até valueShare%; o produto gatilho completa o restante até 100%.
 */
function computeGiftProgressPercent(cartTotal, minValue, options) {
  const opts = options || {};
  const valueShare = typeof opts.valueShare === 'number' ? opts.valueShare : 80;
  const triggerShare = typeof opts.triggerShare === 'number' ? opts.triggerShare : 20;
  const needsTrigger = Boolean(opts.needsTrigger);
  const hasTrigger = Boolean(opts.hasTrigger);

  if (!minValue || minValue <= 0) {
    return 0;
  }

  const valueRatio = Math.min(Math.max(Number(cartTotal) / minValue, 0), 1);

  if (!needsTrigger) {
    return Math.min(100, Math.round(valueRatio * 100));
  }

  return Math.min(
    100,
    Math.round(valueRatio * valueShare + (hasTrigger ? triggerShare : 0))
  );
}

if (typeof window !== 'undefined' && typeof window.computeGiftProgressPercent !== 'function') {
  window.computeGiftProgressPercent = computeGiftProgressPercent;
}

/**
 * Resolve a copy da barra por estado (mesma regra de gift-system.js).
 */
function resolveGiftProgressMessage(state, config, options) {
  const opts = options || {};
  const remaining = typeof opts.remaining === 'number' ? opts.remaining : 0;
  const levelNumber = opts.levelNumber != null ? opts.levelNumber : '';
  const formatCurrency = typeof opts.formatCurrency === 'function'
    ? opts.formatCurrency
    : (v) => String(v);

  const fill = (template) => String(template || '')
    .replace(/\{valor\}/g, formatCurrency(Math.max(remaining, 0)))
    .replace(/\{nivel\}/g, String(levelNumber));

  if (state === 'unlocked') {
    return fill(config && config.messageUnlockedSingle
      ? config.messageUnlockedSingle
      : '🎁 Parabéns! Você ganhou um brinde!');
  }

  if (state === 'trigger_missing') {
    const productFallback = 'Adicione um dos produtos especiais para desbloquear o brinde!';
    let template = (config && config.messageTriggerMissing) || productFallback;

    const looksLikeMoneyLeft =
      /\{valor\}/i.test(template) ||
      /falta\s+(apenas\s+)?r\s*\$/i.test(template) ||
      (/falta\s+apenas/i.test(template) && /r\$|reais|valor/i.test(template));

    if (looksLikeMoneyLeft && remaining <= 0) {
      const progressMsg = (config && config.messageProgress) || '';
      if (progressMsg && !/\{valor\}/i.test(progressMsg)) {
        template = progressMsg;
      } else {
        template = productFallback;
      }
    }

    return fill(template);
  }

  return fill(
    (config && config.messageProgress) || 'Falta apenas {valor} para ganhar um brinde!'
  );
}

if (typeof window !== 'undefined' && typeof window.resolveGiftProgressMessage !== 'function') {
  window.resolveGiftProgressMessage = resolveGiftProgressMessage;
}

class GiftProgressComponent extends HTMLElement {
  constructor() {
    super();
    this.progressBar = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.progressBar?.querySelector('.text');
    this.messageEl = this.querySelector('[data-gift-message]');
    this.progressContainer = this.querySelector('[data-gift-progress]');
  }

  connectedCallback() {
    // Re-bind DOM: constructor pode rodar antes dos filhos existirem
    this.progressBar = this.querySelector('[data-gift-progress-meter]');
    this.progressText = this.progressBar ? this.progressBar.querySelector('.text') : null;
    this.messageEl = this.querySelector('[data-gift-message]');
    this.progressContainer = this.querySelector('[data-gift-progress]');

    // Aplicar cores personalizadas
    this.applyCustomColors();

    // Com escolha ligada, o coordenador dona o snapshot (sem fetch próprio)
    if (window.giftSystemConfig?.choiceEnabled === true) {
      this.connectChoiceCoordinatorPresenter();
      return;
    }

    // Armazenar referência do listener para poder remover depois
    this.boundUpdateProgress = this.updateProgress.bind(this);

    // Escutar eventos de atualização do carrinho
    document.addEventListener('cart-update', this.boundUpdateProgress);

    // Atualizar na inicialização com delay para garantir que config está carregado
    setTimeout(() => this.updateProgress(), 500);
  }

  connectChoiceCoordinatorPresenter() {
    const coordinator = window.GiftChoiceCoordinator;

    if (
      !coordinator ||
      coordinator.__giftChoiceCoordinator !== true ||
      typeof coordinator.registerPresenter !== 'function'
    ) {
      // Coordenador ainda não subiu: fallback legado para não deixar a barra morta
      setTimeout(() => {
        if (window.giftSystemConfig?.choiceEnabled === true) {
          const ok = this.connectChoiceCoordinatorPresenter();
          if (!ok && !this.boundUpdateProgress) {
            this.boundUpdateProgress = this.updateProgress.bind(this);
            document.addEventListener('cart-update', this.boundUpdateProgress);
            this.updateProgress();
          }
        }
      }, 300);
      return false;
    }

    coordinator.registerPresenter(this);
    this.choiceCoordinator = coordinator;
    return true;
  }

  /**
   * Aplica cores personalizadas via CSS variables
   */
  applyCustomColors() {
    const config = window.giftSystemConfig;
    if (!config || !config.colors) {
      return;
    }

    const colors = config.colors;

    // Aplicar CSS variables no elemento raiz (haloCalculatorGift)
    if (this.style) {
      this.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
      this.style.setProperty('--gift-message-color-default', colors.messageDefault || '#666666');
      this.style.setProperty('--gift-message-color-30', colors.message30 || '#a78bfa');
      this.style.setProperty('--gift-message-color-60', colors.message60 || '#8b5cf6');
      this.style.setProperty('--gift-message-color-100', colors.message100 || '#7c3aed');
      this.style.setProperty('--gift-message-color-unlocked', colors.messageUnlocked || '#7c3aed');
    }

    // Também aplicar no progress container se existir
    if (this.progressContainer && this.progressContainer.style) {
      this.progressContainer.style.setProperty('--gift-progress-color-30', colors.progress30 || '#a78bfa');
      this.progressContainer.style.setProperty('--gift-progress-color-60', colors.progress60 || '#8b5cf6');
      this.progressContainer.style.setProperty('--gift-progress-color-100', colors.progress100 || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-color-unlocked', colors.progressUnlocked || '#7c3aed');
      this.progressContainer.style.setProperty('--gift-progress-bg-color', colors.progressBg || '#f0f0f0');
      this.progressContainer.style.setProperty('--gift-progress-text-color', colors.progressText || '#ffffff');
    }
  }

  async updateProgress() {
    try {
      // Reaplicar cores personalizadas (caso tenham mudado)
      this.applyCustomColors();

      // Usar configuração global do gift system
      const config = window.giftSystemConfig;

      if (!config || !config.enabled || !config.levels || config.levels.length === 0) {
        this.style.display = 'none';
        return;
      }

      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateProgressFromCart(cart);
    } catch (error) {
      // Erro silencioso
    }
  }

  renderGiftCoordinatorState({ cart } = {}) {
    this.applyCustomColors();
    if (!cart || !Array.isArray(cart.items)) {
      return;
    }
    // Recicla o pipeline de updateProgress com snapshot já resolvido
    this.updateProgressFromCart(cart);
  }

  updateProgressFromCart(cart) {
    // Re-bind DOM se necessário
    if (!this.progressBar || !this.messageEl) {
      this.progressBar = this.querySelector('[data-gift-progress-meter]');
      this.progressText = this.progressBar ? this.progressBar.querySelector('.text') : null;
      this.messageEl = this.querySelector('[data-gift-message]');
      this.progressContainer = this.querySelector('[data-gift-progress]');
    }

    const config = window.giftSystemConfig;
    if (!config || !config.enabled || !config.levels || config.levels.length === 0) {
      this.style.display = 'none';
      return;
    }


      // Filtrar apenas níveis ativos
      const activeLevels = config.levels.filter(level => level.enabled && level.availableGifts && level.availableGifts.length > 0);

      if (activeLevels.length === 0) {
        this.style.display = 'none';
        return;
      }

      // Mesma base da /cart: original_line_price em reais, excluindo brindes
      const cartTotal = this.getEligibleCartTotalReais(cart);

      const sortedLevels = [...activeLevels].sort((a, b) => a.minValue - b.minValue);
      const nextLevel = sortedLevels.find(level => cartTotal < level.minValue);
      const currentLevel = sortedLevels.filter(level => cartTotal >= level.minValue).pop();

      if (!nextLevel && !currentLevel) {
        this.style.display = 'none';
        return;
      }

      this.style.display = 'block';

      if (currentLevel && !nextLevel) {
        const needsTrigger = currentLevel.triggerProducts && currentLevel.triggerProducts.length > 0;
        const hasTrigger = this.hasTriggerProductInCart(currentLevel, cart);
        const isFullyUnlocked = this.checkLevelEligibility(currentLevel, cart);
        const progress = computeGiftProgressPercent(cartTotal, currentLevel.minValue, {
          needsTrigger,
          hasTrigger
        });

        const formatCurrency = (v) => v.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        });

        if (isFullyUnlocked) {
          const message = resolveGiftProgressMessage('unlocked', config, {
            remaining: 0,
            levelNumber: currentLevel.levelNumber,
            formatCurrency
          });
          this.updateUI(100, message, 'progress-free', cart);
        } else {
          // Valor OK, falta gatilho: fill <100%, copy de PRODUTO (não de R$)
          const message = resolveGiftProgressMessage('trigger_missing', config, {
            remaining: 0,
            levelNumber: currentLevel.levelNumber,
            formatCurrency
          });
          this.updateUI(progress, message, 'progress-100', cart);
        }
      } else if (nextLevel) {
        // nextLevel existe => cartTotal < minValue (só "falta valor")
        const needsTrigger = nextLevel.triggerProducts && nextLevel.triggerProducts.length > 0;
        const hasTrigger = this.hasTriggerProductInCart(nextLevel, cart);
        const progress = computeGiftProgressPercent(cartTotal, nextLevel.minValue, {
          needsTrigger,
          hasTrigger
        });
        const remaining = Math.max(nextLevel.minValue - cartTotal, 0);
        const formatCurrency = (v) => v.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        });

        const message = resolveGiftProgressMessage('progress', config, {
          remaining,
          levelNumber: nextLevel.levelNumber,
          formatCurrency
        });
        this.updateUI(progress, message, this.progressClassForPercent(progress), cart);
      } else {
        this.style.display = 'none';
      }
  }


  /**
   * Total elegível em reais: original_line_price (pré-desconto), ignora _is_gift.
   * Alinha quick cart com gift-system.js da página /cart.
   */
  getEligibleCartTotalReais(cart) {
    if (!cart || !Array.isArray(cart.items)) {
      return 0;
    }

    const cartTotalCents = cart.items.reduce((total, item) => {
      if (item.properties && item.properties._is_gift === 'true') {
        return total;
      }
      return total + (item.original_line_price || item.line_price || 0);
    }, 0);

    return cartTotalCents / 100;
  }

  progressClassForPercent(progress) {
    if (progress >= 100) return 'progress-100';
    if (progress >= 60) return 'progress-60';
    return 'progress-30';
  }

  updateUI(progress, message, progressClass, cart) {
    const safeProgress = Math.min(100, Math.max(0, Math.round(progress)));

    if (this.progressBar) {
      this.progressBar.style.width = `${safeProgress}%`;
    }

    if (this.progressText) {
      this.progressText.textContent = `${safeProgress}%`;
    }

    if (this.messageEl) {
      this.messageEl.textContent = message;
      this.messageEl.className = `message ${progressClass}`;
    }

    if (this.progressContainer) {
      this.progressContainer.className = `progress ${progressClass}`;
    }

    this.syncTriggerPickerButton(cart);
  }

  syncTriggerPickerButton(cart) {
    if (window.GiftTriggerPicker && typeof window.GiftTriggerPicker.syncButtons === 'function') {
      window.GiftTriggerPicker.syncButtons(cart);
      return;
    }
    const wrap = this.querySelector('[data-gift-trigger-picker-actions]');
    if (!wrap) return;
    const config = window.giftSystemConfig;
    if (!config || !config.enabled || (config.triggerPicker && config.triggerPicker.enabled === false)) {
      wrap.hidden = true;
      return;
    }
    // Sem API do picker: deixa oculto até o script carregar
    wrap.hidden = true;
  }

  /**
   * Só produto gatilho (sem checar valor).
   */
  hasTriggerProductInCart(level, cart) {
    if (!level || !level.triggerProducts || level.triggerProducts.length === 0) {
      return true;
    }
    if (!cart || !cart.items) {
      return false;
    }

    return cart.items.some(item => {
      if (item.properties && item.properties._is_gift === 'true') {
        return false;
      }

      return level.triggerProducts.some(triggerProduct => {
        const productIdentifier = typeof triggerProduct === 'object' && triggerProduct !== null
          ? triggerProduct.handle
          : triggerProduct;

        const cleanIdentifier = productIdentifier && productIdentifier.toString().includes('gid://shopify/Product/')
          ? productIdentifier.split('/').pop()
          : productIdentifier;

        const matchById = item.product_id == cleanIdentifier ||
          item.product_id === parseInt(cleanIdentifier, 10);
        const matchByHandle = item.handle &&
          (item.handle === cleanIdentifier || item.handle === productIdentifier);

        return matchById || matchByHandle;
      });
    });
  }

  /**
   * Verifica se um nível está completamente desbloqueado
   * (valor mínimo + trigger products se configurado)
   */
  checkLevelEligibility(level, cart) {
    if (!cart) {
      return false;
    }

    const cartTotal = this.getEligibleCartTotalReais(cart);
    if (cartTotal < level.minValue) {
      return false;
    }

    // Se não tem trigger products configurado, só valor já basta
    if (!level.triggerProducts || level.triggerProducts.length === 0) {
      return true;
    }

    return this.hasTriggerProductInCart(level, cart);
  }

  /**
   * Limpa event listeners quando o elemento é removido do DOM
   */
  disconnectedCallback() {
    if (
      this.choiceCoordinator &&
      typeof this.choiceCoordinator.unregisterPresenter === 'function'
    ) {
      this.choiceCoordinator.unregisterPresenter(this);
      this.choiceCoordinator = null;
    }

    if (this.boundUpdateProgress) {
      document.removeEventListener('cart-update', this.boundUpdateProgress);
    }
  }
}

if (
  typeof customElements !== 'undefined' &&
  !customElements.get('gift-progress-component')
) {
  customElements.define('gift-progress-component', GiftProgressComponent);
}

